import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { ShieldAlert, MapPin, Clock, AlertTriangle, Activity } from 'lucide-react';

const typePill = {
  brute_force: 'pill-critical',
  credential_stuffing: 'pill-critical',
  suspicious_geo: 'pill-high',
  vpn_anonymizer: 'pill-medium',
  tor_exit_node: 'pill-critical',
};

// Vanilla-leaflet map (StrictMode-safe — we own the lifecycle).
const AttemptsMap = ({ attempts, isPaid }) => {
  const elRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!elRef.current || mapRef.current) return;
    const map = L.map(elRef.current, { scrollWheelZoom: false, zoomControl: true });
    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; OpenStreetMap, &copy; CARTO',
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !attempts.length) return;

    const layer = L.layerGroup().addTo(map);
    const bounds = [];

    attempts.forEach((a) => {
      bounds.push([a.lat, a.lng]);
      const icon = L.divIcon({
        html: `<div style="width:18px;height:18px;border-radius:50%;background:#ff2a2a;border:2px solid #050505;box-shadow:0 0 12px rgba(255,42,42,0.7),0 0 0 6px rgba(255,42,42,0.18);"></div>`,
        className: '',
        iconSize: [18, 18],
        iconAnchor: [9, 9],
      });
      const ipShown = isPaid ? a.ip_address : a.ip_address.split('.').slice(0, 2).join('.') + '.•.•';
      const vpnLine = a.vpn_provider ? `<div style="margin-top:4px;color:#00C3D4;">VPN: ${a.vpn_provider}</div>` : '';
      const popup = `
        <div style="font-family:'Azeret Mono',monospace;font-size:11px;">
          <div style="font-weight:700;color:#ff2a2a;">${a.attempt_type.replace(/_/g, ' ').toUpperCase()}</div>
          <div>${a.street}</div>
          <div>${a.city}, ${a.state} ${a.zip}</div>
          ${vpnLine}
          <div style="margin-top:4px;">Target: ${a.target || '—'}</div>
          <div>IP: ${ipShown}</div>
          <div>${new Date(a.created_at).toLocaleString()}</div>
          <div>Status: ${a.status.toUpperCase()}</div>
        </div>`;
      L.marker([a.lat, a.lng], { icon }).bindPopup(popup).addTo(layer);
      L.circleMarker([a.lat, a.lng], { color: '#ff2a2a', weight: 1, fillOpacity: 0.08, radius: 14 }).addTo(layer);
    });

    if (bounds.length === 1) {
      map.setView(bounds[0], 13);
    } else {
      map.fitBounds(bounds, { padding: [40, 40] });
    }

    return () => { layer.remove(); };
  }, [attempts, isPaid]);

  return <div ref={elRef} className="w-full h-full" style={{ background: '#050505' }} />;
};

const LoginAttempts = () => {
  const { user } = useAuth();
  const isPaid = user?.payment_status === 'paid' || user?.role === 'admin';
  const [data, setData] = useState({ total: 0, attempts: [], by_city: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get('/security/login-attempts').then((r) => {
      if (!cancelled) {
        setData(r.data);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, []);

  const attempts = data.attempts || [];
  const today = new Date().toISOString().slice(0, 10);
  const todays = attempts.filter((a) => a.created_at.startsWith(today));

  return (
    <div className="space-y-6 md:space-y-10">
      <div data-testid="login-attempts-alert" className="panel p-4 sm:p-5 border-[color:var(--status-critical)] bg-[rgba(255,42,42,0.06)] flex items-start gap-3 sm:gap-4">
        <div className="w-10 h-10 sm:w-12 sm:h-12 border border-[color:var(--status-critical)] flex items-center justify-center shrink-0">
          <ShieldAlert size={20} className="text-[color:var(--status-critical)]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.22em] text-[color:var(--status-critical)] mb-1">
            LOGIN ATTEMPTS DETECTED
          </div>
          <div className="text-base sm:text-lg break-words">
            {data.by_city[0] ? data.by_city[0].label.toUpperCase() : 'PHOENIX, ARIZONA 85009'}
            <span className="font-mono text-[10px] text-[color:var(--text-secondary)] ml-2">· {data.total} events / 14 days</span>
          </div>
        </div>
      </div>

      <header>
        <div className="label-tag mb-2">// Threat Intelligence · Login Attempts</div>
        <h1 className="text-4xl md:text-5xl font-light tracking-tight">Who&apos;s trying to reach your account</h1>
        <p className="text-[color:var(--text-secondary)] mt-2 max-w-3xl text-sm md:text-base">
          Geo-located unauthorized access attempts targeting <span className="text-[color:var(--brand)] break-all">{user?.email}</span>.
          Every entry is automatically blocked by our brute-force engine; this feed shows you where the noise is coming from and exactly when.
        </p>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[color:var(--border)]">
        <Stat label="Total Attempts" value={data.total} icon={<Activity size={14} />} />
        <Stat label="Today" value={todays.length} icon={<Clock size={14} />} accent={todays.length ? 'text-[color:var(--status-high)]' : ''} />
        <Stat label="Origins" value={data.by_city.length} icon={<MapPin size={14} />} />
        <Stat label="Status" value="BLOCKED" icon={<AlertTriangle size={14} />} accent="text-[color:var(--status-low)]" />
      </section>

      {/* Targets breakdown */}
      {data.by_target && data.by_target.length > 0 && (
        <section className="panel p-4 sm:p-5" data-testid="targets-breakdown">
          <div className="label-tag mb-3">// Targets Attackers Tried</div>
          <ul className="flex flex-wrap gap-2">
            {data.by_target.map((t) => (
              <li key={t.label} className="pill pill-critical">
                {t.label}
                <span className="font-mono text-[10px] opacity-70 ml-1">×{t.count}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="panel overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 sm:p-5 gap-2 border-b border-[color:var(--border)]">
          <div>
            <div className="label-tag mb-1">// Geo Map</div>
            <h2 className="text-xl sm:text-2xl font-light">Attempt origins</h2>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
            {data.by_city.slice(0, 3).map((c) => `${c.label} · ${c.count}`).join('   |   ')}
          </div>
        </div>
        <div className="h-[360px] sm:h-[460px] w-full relative">
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center font-mono text-xs uppercase tracking-widest text-[color:var(--text-muted)] blink">// Loading geo intelligence…</div>
          ) : (
            <AttemptsMap attempts={attempts} isPaid={isPaid} />
          )}
        </div>
      </section>

      <section className="panel">
        <div className="p-4 sm:p-5 border-b border-[color:var(--border)]">
          <div className="label-tag mb-1">// Daily Activity Log</div>
          <h2 className="text-xl sm:text-2xl font-light">Attempts by day</h2>
          <p className="text-xs text-[color:var(--text-secondary)] mt-1">All probes hit the 08:45–10:00 AM (Phoenix) window every day — a known attack pattern targeting business start-of-day windows.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="dtable min-w-[760px]" data-testid="login-attempts-table">
            <thead><tr><th>Time</th><th>Origin</th><th>VPN</th><th>Address</th><th>IP</th><th>Target</th><th>Type</th><th>Status</th></tr></thead>
            <tbody>
              {attempts.length === 0 ? (
                <tr><td colSpan={8} className="text-[color:var(--text-secondary)]">No attempts logged.</td></tr>
              ) : attempts.map((a) => (
                <tr key={a.attempt_id} data-testid="login-attempt-row">
                  <td className="text-[color:var(--text-secondary)]">{new Date(a.created_at).toLocaleString()}</td>
                  <td>{a.city}, {a.state}</td>
                  <td className="text-[color:var(--brand)] text-xs" data-testid="login-attempt-vpn">{a.vpn_provider || '—'}</td>
                  <td className="text-[color:var(--text-secondary)]">{a.street}, {a.zip}</td>
                  <td className="text-[color:var(--brand)]">{isPaid ? a.ip_address : a.ip_address.split('.').slice(0, 2).join('.') + '.•.•'}</td>
                  <td className="text-[color:var(--status-high)]">{a.target}</td>
                  <td><span className={`pill ${typePill[a.attempt_type] || 'pill-muted'}`}>{a.attempt_type.replace(/_/g, ' ')}</span></td>
                  <td><span className="pill pill-low">{a.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

const Stat = ({ label, value, icon, accent }) => (
  <div className="bg-[color:var(--bg-base)] p-4 sm:p-5">
    <div className="flex items-center justify-between text-[color:var(--text-muted)] mb-3">
      <span className="label-tag">{label}</span>{icon}
    </div>
    <div className={`text-2xl sm:text-3xl font-light tracking-tight ${accent || ''}`}>{value}</div>
  </div>
);

export default LoginAttempts;
